import React, { useState, useEffect, useMemo } from 'react';
import { Search, User, Receipt, IndianRupee, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Student, SchoolClass, StudentFee } from '../../types';
import { studentsApi, schoolDataApi, feesApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';
import { formatCurrency, billTitle, billTypeBadgeStyle, billStatusBadgeStyle } from '../../utils/helpers';

interface FeeCollectionFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const today = () => new Date().toISOString().split('T')[0];

const sortBills = (bills: StudentFee[]) =>
  [...bills].sort((a, b) => {
    const yearCompare = b.academicYear.localeCompare(a.academicYear);
    if (yearCompare !== 0) return yearCompare;
    const monthCompare = (b.month || '').localeCompare(a.month || '');
    if (monthCompare !== 0) return monthCompare;
    return (a.billType || 'monthly').localeCompare(b.billType || 'monthly');
  });

const FeeCollectionForm: React.FC<FeeCollectionFormProps> = ({ onSuccess, onCancel }) => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [classFilter, setClassFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [bills, setBills] = useState<StudentFee[]>([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [selectedBill, setSelectedBill] = useState<StudentFee | null>(null);

  const [amount, setAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState<'Cash' | 'Online' | 'Cheque' | 'Bank Transfer'>('Cash');
  const [transactionId, setTransactionId] = useState('');
  const [date, setDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    schoolDataApi.classes.getAll().then(data => setClasses(sortClasses(data))).catch(() => setClasses([]));
    studentsApi.getAll().then(setAllStudents).catch(() => setAllStudents([]));
  }, []);

  const sectionsForClassFilter = useMemo(
    () => classes.find(c => c.name === classFilter)?.sections || [],
    [classes, classFilter]
  );

  const matchingStudents = useMemo(() => {
    if (!classFilter && !sectionFilter && searchQuery.trim().length < 2) return [];

    let list = allStudents.filter(s => s.admissionStatus === 'Approved');
    if (classFilter) list = list.filter(s => s.class === classFilter);
    if (sectionFilter) list = list.filter(s => s.section === sectionFilter);
    if (searchQuery.trim().length >= 2) {
      const term = searchQuery.toLowerCase();
      list = list.filter(
        s =>
          s.name.toLowerCase().includes(term) ||
          s.studentId.toLowerCase().includes(term) ||
          s.parentContact.includes(searchQuery)
      );
    }
    return list.slice(0, 20);
  }, [allStudents, classFilter, sectionFilter, searchQuery]);

  const loadBills = async (studentId: string) => {
    try {
      setLoadingBills(true);
      const data = await schoolDataApi.studentFees.getAllByStudentId(studentId);
      setBills(sortBills(data));
    } catch (error) {
      toast.error('Failed to load bills for this student');
      console.error('Error loading bills:', error);
    } finally {
      setLoadingBills(false);
    }
  };

  const selectStudent = (student: Student) => {
    setSelectedStudent(student);
    setSearchQuery('');
    setSelectedBill(null);
    loadBills(student.id);
  };

  const changeStudent = () => {
    setSelectedStudent(null);
    setBills([]);
    setSelectedBill(null);
  };

  const selectBill = (bill: StudentFee) => {
    if (bill.status === 'Paid') return;
    setSelectedBill(bill);
    setAmount(bill.pendingAmount);
    setPaymentMode('Cash');
    setTransactionId('');
    setDate(today());
  };

  const resetAll = () => {
    setSelectedStudent(null);
    setBills([]);
    setSelectedBill(null);
    setAmount(0);
    setPaymentMode('Cash');
    setTransactionId('');
    setDate(today());
    setClassFilter('');
    setSectionFilter('');
    setSearchQuery('');
  };

  const handleCancel = () => {
    resetAll();
    onCancel();
  };

  const handleSubmit = async () => {
    if (!selectedStudent || !selectedBill) return;
    if (!amount || amount <= 0 || amount > selectedBill.pendingAmount) {
      toast.error(`Amount must be between ₹1 and ${formatCurrency(selectedBill.pendingAmount)}`);
      return;
    }
    if (date > today()) {
      toast.error('Payment date cannot be in the future');
      return;
    }
    if (paymentMode !== 'Cash' && !transactionId.trim()) {
      toast.error('Transaction / reference ID is required for non-cash payments');
      return;
    }

    try {
      setSubmitting(true);
      await feesApi.processPayment(
        {
          studentId: selectedStudent.id,
          studentFeeId: selectedBill.id,
          amount,
          paymentMode,
          transactionId: transactionId.trim() || undefined,
          date
        },
        'Admin' // TODO: Pass actual admin user name
      );
      toast.success('Fee payment collected successfully');
      onSuccess();
      setSelectedBill(null);
      await loadBills(selectedStudent.id);
    } catch (error) {
      toast.error('Failed to collect fee payment');
      console.error('Error collecting payment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Student search */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Search Student</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative sm:col-span-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7f6e]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name, student ID, or parent contact..."
              className="w-full pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>
          <select
            value={classFilter}
            onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(''); }}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
          >
            <option value="">All Classes</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.name}>{cls.name}</option>
            ))}
          </select>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            disabled={sectionsForClassFilter.length === 0}
            className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">All Sections</option>
            {sectionsForClassFilter.map(section => (
              <option key={section} value={section}>Section {section}</option>
            ))}
          </select>
        </div>

        {!selectedStudent && matchingStudents.length > 0 && (
          <div className="mt-3 border border-[#3a3122] rounded-lg bg-[#17140f] max-h-64 overflow-y-auto">
            {matchingStudents.map(student => (
              <div
                key={student.id}
                onClick={() => selectStudent(student)}
                className="p-3 hover:bg-[#28221a] cursor-pointer border-b border-[#3a3122] last:border-b-0 flex items-center gap-3"
              >
                <div className="w-8 h-8 bg-[#16212b] rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-[#60a5fa]" />
                </div>
                <div>
                  <p className="font-medium text-[#f3ede2] text-sm">{student.name}</p>
                  <p className="text-xs text-[#8a7f6e]">
                    {student.studentId} &middot; {student.class}{student.section ? ` - ${student.section}` : ''} &middot; {student.parentContact}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {!selectedStudent && !classFilter && !sectionFilter && searchQuery.trim().length < 2 && (
          <p className="text-xs text-[#8a7f6e] mt-2">Search by name, or filter by class and section, to find a student.</p>
        )}
      </div>

      {/* Selected student + bills */}
      {selectedStudent && (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[#f3ede2] font-semibold">{selectedStudent.name}</h3>
              <p className="text-xs text-[#8a7f6e]">
                {selectedStudent.studentId} &middot; {selectedStudent.class}{selectedStudent.section ? ` - ${selectedStudent.section}` : ''}
              </p>
            </div>
            <button
              onClick={changeStudent}
              className="text-sm text-[#b9ad9b] hover:text-[#f3ede2] border border-[#3a3122] rounded-lg px-3 py-1.5 hover:bg-[#28221a] transition-colors"
            >
              Change Student
            </button>
          </div>

          {loadingBills ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-10">
              <Receipt className="w-10 h-10 text-[#8a7f6e] mx-auto mb-3" />
              <p className="text-[#b9ad9b]">No bills generated for this student yet</p>
              <p className="text-[#8a7f6e] text-sm">Generate a bill from Fee Management → Generate Bill</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-[#3a3122] rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[#28221a]">
                  <tr>
                    <th className="text-left py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Bill</th>
                    <th className="text-left py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Type</th>
                    <th className="text-right py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Pending</th>
                    <th className="text-left py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3a3122]">
                  {bills.map(bill => {
                    const isPaid = bill.status === 'Paid';
                    const isSelected = selectedBill?.id === bill.id;
                    return (
                      <tr
                        key={bill.id}
                        onClick={() => selectBill(bill)}
                        className={`${isPaid ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#28221a]'} ${
                          isSelected ? 'bg-[#332217]' : ''
                        } transition-colors`}
                      >
                        <td className="py-2.5 px-4">
                          <p className="text-[#f3ede2] font-medium">{billTitle(bill)}</p>
                          <p className="text-xs text-[#8a7f6e]">{bill.className} &middot; {bill.academicYear}</p>
                        </td>
                        <td className="py-2.5 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${billTypeBadgeStyle(bill)}`}>
                            {(bill.billType || 'monthly').charAt(0).toUpperCase() + (bill.billType || 'monthly').slice(1)}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-right text-[#f3ede2] font-medium">{formatCurrency(bill.pendingAmount)}</td>
                        <td className="py-2.5 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${billStatusBadgeStyle(bill.status)}`}>
                            {bill.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment details for the selected bill */}
      {selectedBill && (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
          <h3 className="text-[#f3ede2] font-semibold mb-1">Record Payment — {billTitle(selectedBill)}</h3>
          <p className="text-xs text-[#8a7f6e] mb-5">
            Total {formatCurrency(selectedBill.totalFees)} &middot; Paid {formatCurrency(selectedBill.paidAmount)} &middot; Pending {formatCurrency(selectedBill.pendingAmount)}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Amount *</label>
              <div className="relative">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7f6e]" />
                <input
                  type="number"
                  min={1}
                  max={selectedBill.pendingAmount}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Payment Mode *</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as typeof paymentMode)}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              >
                <option value="Cash">Cash</option>
                <option value="Online">Online</option>
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Payment Date *</label>
              <input
                type="date"
                value={date}
                max={today()}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>

            {paymentMode !== 'Cash' && (
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Transaction / Reference ID *</label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Enter transaction or reference ID"
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-[#3a3122]">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
            >
              <X className="w-4 h-4 inline-block mr-2" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center space-x-2 px-4 py-2 bg-[#4ade80] hover:bg-[#6ee7a0] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Receipt className="w-4 h-4" />
              <span>{submitting ? 'Processing...' : 'Collect Payment'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeCollectionForm;
